import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, IconButton, Menu, MenuItem, Paper, Typography } from "@mui/material";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { PullToRefresh } from "../general/PullToRefresh";
import { MealPlanEntry } from "../../models/MealPlanEntry";
import { Ingredient } from "../../models/Ingredient";
import { Recipe } from "../../models/Recipe";
import { getMealPlanForWeek, deleteMealPlanEntry } from "../../utils/MealPlanService";
import { getRecipeById } from "../../utils/RecipeService";
import MealPlanCell from "./MealPlanCell";
import RecipePickerDialog from "./RecipePickerDialog";
import AddIngredientsToShoppingListDialog from "../recipe/AddIngredientsToShoppingListDialog";

const MEAL_TYPES: Array<'BREAKFAST' | 'LUNCH' | 'DINNER'> = ['BREAKFAST', 'LUNCH', 'DINNER'];

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getMonday(date: Date): Date {
  const result = new Date(date);
  const dayOfWeek = result.getDay();
  result.setDate(result.getDate() + (dayOfWeek === 0 ? -6 : 1 - dayOfWeek));
  return result;
}

type PickerSlot = { date: string; mealType: 'BREAKFAST' | 'LUNCH' | 'DINNER' };

const MealPlanner = () => {
  const { t } = useTranslation();

  const [weekOffset, setWeekOffset] = useState(0);

  const weekDates = useMemo(() => {
    const monday = getMonday(new Date());
    monday.setDate(monday.getDate() + weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  }, [weekOffset]);

  const [entries, setEntries] = useState<MealPlanEntry[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSlot, setPickerSlot] = useState<PickerSlot | null>(null);
  const [pickerEntry, setPickerEntry] = useState<MealPlanEntry | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuEntry, setMenuEntry] = useState<MealPlanEntry | null>(null);
  const [shoppingDialogOpen, setShoppingDialogOpen] = useState(false);
  const [shoppingIngredients, setShoppingIngredients] = useState<Ingredient[]>([]);
  const [shoppingFactor, setShoppingFactor] = useState(1);

  const loadWeek = useCallback(() => {
    getMealPlanForWeek(toIsoDate(weekDates[0])).then(setEntries);
  }, [weekDates]);

  useEffect(() => { loadWeek(); }, [loadWeek]);

  const entryFor = useCallback((dateIso: string, mealType: string) =>
    entries.find(e => e.date === dateIso && e.mealType === mealType),
    [entries]);

  const openPickerForEmptySlot = useCallback((dateIso: string, mealType: 'BREAKFAST' | 'LUNCH' | 'DINNER') => {
    setPickerSlot({ date: dateIso, mealType });
    setPickerEntry(null);
    setPickerOpen(true);
  }, []);

  const openMenuForEntry = useCallback((anchorEl: HTMLElement, entry: MealPlanEntry) => {
    setMenuAnchor(anchorEl);
    setMenuEntry(entry);
  }, []);

  const closeMenu = useCallback(() => { setMenuAnchor(null); setMenuEntry(null); }, []);

  const changeRecipe = useCallback(() => {
    if (menuEntry) {
      setPickerSlot({ date: menuEntry.date, mealType: menuEntry.mealType });
      setPickerEntry(menuEntry);
      setPickerOpen(true);
    }
    closeMenu();
  }, [menuEntry, closeMenu]);

  const removeEntry = useCallback(() => {
    if (menuEntry?.id !== null) deleteMealPlanEntry(menuEntry!.id!).then(loadWeek);
    closeMenu();
  }, [menuEntry, closeMenu, loadWeek]);

  const addToShoppingList = useCallback(() => {
    if (menuEntry) {
      getRecipeById(String(menuEntry.recipeId)).then((recipe: Recipe | null) => {
        if (recipe) {
          setShoppingIngredients(recipe.ingredients);
          setShoppingFactor(menuEntry.servings / recipe.yield);
          setShoppingDialogOpen(true);
        }
      });
    }
    closeMenu();
  }, [menuEntry, closeMenu]);

  return (
    <PullToRefresh
      onRefresh={loadWeek}
      style={{ height: 'calc(100vh - 64px)' }}
      contentStyle={{ display: 'flex', flexDirection: 'column', padding: '12px' }}
    >
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Week navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton size="small" onClick={() => setWeekOffset(o => o - 1)}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>
          {weekDates[0].toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
          {' – '}
          {weekDates[6].toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
        </Typography>
        <IconButton size="small" onClick={() => setWeekOffset(o => o + 1)}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Column headers */}
      <Box sx={{ display: 'flex', pl: '72px', mb: 1 }}>
        {MEAL_TYPES.map(mealType => (
          <Box key={mealType} sx={{ flex: 1, textAlign: 'center' }}>
            <Chip
              label={t(mealType.toLowerCase())}
              size="small"
              sx={{
                fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', height: 22,
                backgroundColor: (theme) => theme.palette.mode === 'light'
                  ? 'rgba(217, 119, 6, 0.1)' : 'rgba(245, 158, 11, 0.12)',
                color: 'primary.main',
              }}
            />
          </Box>
        ))}
      </Box>

      {/* Day rows */}
      {weekDates.map(date => {
        const dateIso = toIsoDate(date);
        const isToday = dateIso === toIsoDate(new Date());
        return (
          <Box key={dateIso} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            {/* Day label */}
            <Box sx={{
              width: 60, flexShrink: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: isToday ? 'primary.main' : 'text.secondary', fontSize: '0.72rem' }}
              >
                {date.toLocaleDateString(undefined, { weekday: 'short' })}
              </Typography>
              <Box sx={{
                width: 26, height: 26, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: isToday ? 'primary.main' : 'transparent',
                color: isToday ? '#fff' : 'text.primary',
              }}>
                <Typography variant="caption" fontWeight={isToday ? 700 : 400} sx={{ fontSize: '0.8rem' }}>
                  {date.getDate()}
                </Typography>
              </Box>
            </Box>

            {/* Meal cells */}
            {MEAL_TYPES.map(mealType => {
              const entry = entryFor(dateIso, mealType);
              return (
                <MealPlanCell
                  key={mealType}
                  entry={entry}
                  dayLabel={`${date.toLocaleDateString(undefined, { weekday: 'long' })} ${date.getDate()}`}
                  mealLabel={t(mealType.toLowerCase())}
                  onEmptyClick={() => openPickerForEmptySlot(dateIso, mealType)}
                  onEntryClick={(anchorEl) => entry && openMenuForEntry(anchorEl, entry)}
                />
              );
            })}
          </Box>
        );
      })}

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem onClick={addToShoppingList}>{t('addToShoppingList')}</MenuItem>
        <MenuItem onClick={changeRecipe}>{t('changeRecipe')}</MenuItem>
        <MenuItem onClick={removeEntry} sx={{ color: 'error.main' }}>{t('removeMeal')}</MenuItem>
      </Menu>

      {pickerSlot && (
        <RecipePickerDialog
          open={pickerOpen}
          setOpen={setPickerOpen}
          date={pickerSlot.date}
          mealType={pickerSlot.mealType}
          existingEntry={pickerEntry}
          onSaved={() => { setPickerOpen(false); loadWeek(); }}
        />
      )}

      <AddIngredientsToShoppingListDialog
        open={shoppingDialogOpen}
        setOpen={setShoppingDialogOpen}
        ingredients={shoppingIngredients}
        factor={shoppingFactor}
      />
    </Box>
    </PullToRefresh>
  );
};

export default MealPlanner;
